import { useEffect, useState } from "react";
import Button from "../Button";
import {
  DropdownMenu,
  DropdownMenuPortal,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuItem,
} from "@radix-ui/react-dropdown-menu";
import pseudoBank from "./scripts/pseudo-bank";
import venmo from "./scripts/venmo";
import venmoTransactions from "./scripts/venmo-transactions";
import reddit from "./scripts/reddit";
import twitter from "./scripts/twitter";
import namecheap from "./scripts/namecheap";
import { DEFAULT_SCRIPT } from "../../constants";
import united from "./scripts/united";

type Script = {
  label: string;
  script: string;
};

const ScriptSelector = ({
  onScriptChange,
  hasChanges,
  editorRef,
}: {
  onScriptChange: (script: string) => void;
  hasChanges: boolean;
  editorRef: React.RefObject<any>;
}) => {
  const [selectedScript, setSelectedScript] = useState<Script>(DEFAULT_SCRIPT);
  const [lastSelectionTime, setLastSelectionTime] = useState<number>(0);

  const handleScriptChange = (script: Script) => {
    setSelectedScript(script);
    onScriptChange(script.script);
    setLastSelectionTime(Date.now());
  };

  useEffect(() => {
    if (hasChanges) {
      setSelectedScript({
        label: "Custom",
        script: editorRef?.current?.getValue() || "",
      });
    } else {
      if (Date.now() - lastSelectionTime > 100) {
        setSelectedScript(DEFAULT_SCRIPT);
      } else {
      }
    }
  }, [hasChanges]);

  // Expose a reset method to parent components
  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.resetScript = () => {
        setSelectedScript(DEFAULT_SCRIPT);
      };
    }

    return () => {
      // Clean up the method when component unmounts
      if (editorRef.current) {
        delete editorRef.current.resetScript;
      }
    };
  }, [editorRef, onScriptChange]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          style={{
            boxShadow:
              "0px 2px 5px rgba(60, 66, 87, 0.08), 0px 1px 1px rgba(0, 0, 0, 0.12)",
          }}
          className="bg-white outline-0 cursor-pointer text-sm font-semibold font-['Stabil_Grotesk'] px-2 py-1 rounded-sm border-[1px] leading-5 tracking-normal relative transition-all duration-150 hover:bg-gray-50 active:transform active:translate-y-[1px] active:shadow-none active:bg-gray-100 text-text-primary border-border flex items-center gap-2"
        >
          {selectedScript.label}
          <span>
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g id="arrowUpDown">
                <g id="Glyph">
                  <path
                    d="M6 1.81262L8.69349 4.50611C8.98665 4.79927 9.46196 4.79927 9.75513 4.50611C10.0483 4.21294 10.0483 3.73763 9.75513 3.44447L6.53033 0.21967C6.23744 -0.0732233 5.76257 -0.0732233 5.46967 0.21967L2.24487 3.44447C1.95171 3.73763 1.95171 4.21294 2.24487 4.50611C2.53804 4.79927 3.01335 4.79927 3.30652 4.50611L6 1.81262Z"
                    fill="#414552"
                  />
                  <path
                    d="M9.75513 7.49389C9.46196 7.20073 8.98665 7.20073 8.69349 7.49389L6 10.1874L3.30652 7.49389C3.01335 7.20073 2.53804 7.20073 2.24487 7.49389C1.95171 7.78706 1.95171 8.26237 2.24487 8.55553L5.46967 11.7803C5.76257 12.0732 6.23744 12.0732 6.53033 11.7803L9.75513 8.55553C10.0483 8.26237 10.0483 7.78706 9.75513 7.49389Z"
                    fill="#414552"
                  />
                </g>
              </g>
            </svg>
          </span>
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuPortal>
        <DropdownMenuContent
          className="bg-white py-2 shadow-lg rounded-sm DropdownMenuContent z-50"
          side="bottom"
          align="end"
          sideOffset={10}
        >
          <DropdownMenuLabel />
          <DropdownMenuItem
            onClick={() => {
              handleScriptChange(reddit);
            }}
            className="text-lg px-4 cursor-pointer outline-0 hover:underline underline-offset-4"
          >
            {reddit.label}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              handleScriptChange(venmoTransactions);
            }}
            className="text-lg px-4 cursor-pointer outline-0 hover:underline underline-offset-4"
          >
            {venmoTransactions.label}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              handleScriptChange(twitter);
            }}
            className="text-lg px-4 cursor-pointer outline-0 hover:underline underline-offset-4"
          >
            {twitter.label}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              handleScriptChange(pseudoBank);
            }}
            className="text-lg px-4 cursor-pointer outline-0 hover:underline underline-offset-4"
          >
            {pseudoBank.label}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              handleScriptChange(united);
            }}
            className="text-lg px-4 cursor-pointer outline-0 hover:underline underline-offset-4"
          >
            {united.label}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </DropdownMenu>
  );
};

export default ScriptSelector;
